import React, { useState } from 'react';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  features: string[];
  isPopular?: boolean;
  isCurrent?: boolean;
  description: string;
  color: string;
}

const MiPlan: React.FC = () => {
  const [currentPlan, setCurrentPlan] = useState('pro');

  const plans: Plan[] = [
    {
      id: 'basico',
      name: 'Básico',
      price: 0,
      period: 'mes',
      description: 'Perfecto para comenzar y probar nuestros servicios',
      color: '#667eea',
      features: [
        'Hasta 10 productos en catálogo',
        'Soporte por email',
        'Estadísticas básicas',
        'Perfil de empresa básico',
        'Acceso a marketplace',
        'Notificaciones por email'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 2999,
      period: 'mes',
      description: 'Ideal para empresas en crecimiento',
      color: '#764ba2',
      isPopular: true,
      isCurrent: true,
      features: [
        'Hasta 100 productos en catálogo',
        'Soporte prioritario por email y chat',
        'Estadísticas avanzadas',
        'Perfil de empresa completo',
        'Acceso completo al marketplace',
        'Notificaciones push y email',
        'Herramientas de marketing básicas',
        'Reportes mensuales',
        'Integración con redes sociales',
        'Personalización de perfil'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 5999,
      period: 'mes',
      description: 'Para empresas establecidas que buscan maximizar su presencia',
      color: '#f093fb',
      features: [
        'Productos ilimitados en catálogo',
        'Soporte 24/7 por teléfono, email y chat',
        'Estadísticas premium con análisis avanzado',
        'Perfil de empresa destacado',
        'Posicionamiento prioritario en marketplace',
        'Notificaciones personalizadas',
        'Herramientas de marketing avanzadas',
        'Reportes personalizados',
        'Integración completa con redes sociales',
        'Personalización completa de perfil',
        'API de acceso para desarrolladores',
        'Consultoría personalizada',
        'Capacitación para tu equipo',
        'Acceso anticipado a nuevas funciones'
      ]
    }
  ];

  const formatPrice = (price: number) => {
    if (price === 0) return 'Gratis';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price);
  };

  const handleUpgrade = (planId: string) => {
    console.log(`Actualizando a plan: ${planId}`);
    // Aquí se implementaría la lógica de actualización
    alert(`¡Gracias por elegir el plan ${plans.find(p => p.id === planId)?.name}! Te contactaremos pronto.`);
  };

  const getCurrentPlan = () => plans.find(p => p.id === currentPlan);

  return (
    <div className="mi-plan-section">
      <div className="section-header">
        <h2>Mi Plan</h2>
        <p>Revisa tu plan actual y descubre las opciones disponibles para tu empresa</p>
      </div>

      {/* Current Plan Status */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Tu Plan Actual</h3>
          <div className="card-actions">
            <span className="status-badge status-active">Activo</span>
          </div>
        </div>
        
        <div className="current-plan-info">
          <div className="current-plan-card">
            <div className="plan-header">
              <h4>{getCurrentPlan()?.name}</h4>
              <div className="plan-price">
                <span className="price-amount">{formatPrice(getCurrentPlan()?.price || 0)}</span>
                <span className="price-period">/{getCurrentPlan()?.period}</span>
              </div>
            </div>
            <p className="plan-description">{getCurrentPlan()?.description}</p>
            <div className="plan-features">
              <h5>Características incluidas:</h5>
              <ul>
                {getCurrentPlan()?.features.slice(0, 6).map((feature, index) => (
                  <li key={index}>
                    <i className="fas fa-check"></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="plan-actions">
              <button className="btn btn-secondary">
                <i className="fas fa-cog"></i>
                Configurar Plan
              </button>
              <button className="btn btn-primary">
                <i className="fas fa-download"></i>
                Descargar Factura
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Available Plans */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Planes Disponibles</h3>
          <p>Elige el plan que mejor se adapte a las necesidades de tu empresa</p>
        </div>

        <div className="plans-grid">
          {plans.map((plan) => (
            <div 
              key={plan.id} 
              className={`plan-card ${plan.isPopular ? 'popular' : ''} ${plan.isCurrent ? 'current' : ''}`}
              style={{ borderColor: plan.color }}
            >
              {plan.isPopular && (
                <div className="popular-badge" style={{ backgroundColor: plan.color }}>
                  Más Popular
                </div>
              )}
              {plan.isCurrent && (
                <div className="current-badge" style={{ backgroundColor: plan.color }}>
                  Plan Actual
                </div>
              )}
              
              <div className="plan-header">
                <h4>{plan.name}</h4>
                <div className="plan-price">
                  <span className="price-amount">{formatPrice(plan.price)}</span>
                  <span className="price-period">/{plan.period}</span>
                </div>
              </div>
              
              <p className="plan-description">{plan.description}</p>
              
              <div className="plan-features">
                <h5>Características:</h5>
                <ul>
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <i className="fas fa-check"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="plan-actions">
                {plan.isCurrent ? (
                  <button className="btn btn-secondary" disabled>
                    <i className="fas fa-check"></i>
                    Plan Actual
                  </button>
                ) : (
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleUpgrade(plan.id)}
                    style={{ backgroundColor: plan.color, borderColor: plan.color }}
                  >
                    <i className="fas fa-arrow-up"></i>
                    {plan.price === 0 ? 'Comenzar Gratis' : 'Actualizar Plan'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plan Comparison */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Comparación de Planes</h3>
          <p>Compara las características de cada plan para tomar la mejor decisión</p>
        </div>

        <div className="plan-comparison">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Característica</th>
                <th>Básico</th>
                <th>Pro</th>
                <th>Premium</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Productos en catálogo</td>
                <td>Hasta 10</td>
                <td>Hasta 100</td>
                <td>Ilimitados</td>
              </tr>
              <tr>
                <td>Soporte</td>
                <td>Email</td>
                <td>Email + Chat</td>
                <td>24/7 Teléfono + Email + Chat</td>
              </tr>
              <tr>
                <td>Estadísticas</td>
                <td>Básicas</td>
                <td>Avanzadas</td>
                <td>Premium con análisis</td>
              </tr>
              <tr>
                <td>Perfil de empresa</td>
                <td>Básico</td>
                <td>Completo</td>
                <td>Destacado</td>
              </tr>
              <tr>
                <td>Posicionamiento</td>
                <td>Estándar</td>
                <td>Mejorado</td>
                <td>Prioritario</td>
              </tr>
              <tr>
                <td>Herramientas de marketing</td>
                <td>No incluidas</td>
                <td>Básicas</td>
                <td>Avanzadas</td>
              </tr>
              <tr>
                <td>API de acceso</td>
                <td>No</td>
                <td>No</td>
                <td>Sí</td>
              </tr>
              <tr>
                <td>Consultoría</td>
                <td>No</td>
                <td>No</td>
                <td>Sí</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Preguntas Frecuentes</h3>
        </div>
        
        <div className="faq-section">
          <div className="faq-item">
            <h4>¿Puedo cambiar de plan en cualquier momento?</h4>
            <p>Sí, puedes actualizar o degradar tu plan en cualquier momento. Los cambios se aplicarán en el próximo ciclo de facturación.</p>
          </div>
          <div className="faq-item">
            <h4>¿Hay un período de prueba gratuito?</h4>
            <p>El plan Básico es completamente gratuito y puedes usarlo sin límite de tiempo. Los planes Pro y Premium incluyen un período de prueba de 14 días.</p>
          </div>
          <div className="faq-item">
            <h4>¿Qué métodos de pago aceptan?</h4>
            <p>Aceptamos tarjetas de crédito, débito, transferencias bancarias y pagos a través de Mercado Pago.</p>
          </div>
          <div className="faq-item">
            <h4>¿Puedo cancelar mi suscripción?</h4>
            <p>Sí, puedes cancelar tu suscripción en cualquier momento desde tu panel de control. No hay penalizaciones por cancelación.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiPlan;
